import Reveal from "@/components/Reveal";
import { Linkedin } from "lucide-react";
import { people } from "@/data/people";

const testimonials = [
  {
    quote: "Before B, I was posting into the void. Six months in, my posts come up in sales calls I'm not even part of. We went from 800 to 6,200 followers without a single gimmick.",
    ...people.aarav,
    h: "lg:min-h-[340px]",
  },
  {
    quote: "B turned ten years of experience into a content system I run in an hour a week.",
    ...people.priya,
    h: "lg:min-h-[270px]",
  },
  {
    quote: "She found the one idea I'd been circling for years and made it unmistakable. The positioning work alone was worth it — everything after became easier.",
    ...people.daniel,
    h: "lg:min-h-[320px]",
  },
  {
    quote: "I was sceptical about 'personal branding'. Then my content started sounding like me on my best day — and three coaching clients found me through it in the first quarter.",
    ...people.elena,
    h: "lg:min-h-[310px]",
  },
  {
    quote: "My profile went from a CV to a channel. 40+ inbound conversations last quarter. I haven't sent a single cold DM.",
    ...people.rohan,
    h: "lg:min-h-[290px]",
  },
  {
    quote: "Clear thinking, sharp execution, no agency theatre. You work with B directly — she handholds the strategy, you never handhold her.",
    ...people.julia,
    h: "lg:min-h-[300px]",
  },
  {
    quote: "B sees the story in your experience before you can see it yourself. My carousel series now averages 30K impressions a post. It used to be 800.",
    ...people.meera,
    h: "lg:min-h-[310px]",
  },
  {
    quote: "Three months of consistency did what two years of random posting couldn't. We closed our first inbound enterprise lead straight from a post.",
    ...people.tomas,
    h: "lg:min-h-[300px]",
  },
  {
    quote: "My LinkedIn and newsletter finally work as one system instead of two chores. Audience up 4x in eight months — and I spend less time on it than before.",
    ...people.ananya,
    h: "lg:min-h-[320px]",
  },
];

const faces = testimonials.slice(0, 8);

export default function Testimonials() {
  return (
    <section id="reviews" className="bg-[#09090B] border-t border-[#F3F0E8]/10 scroll-mt-20">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-28 sm:py-36">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-[#CFE9EF]/70">reviews</p>
          <h2
            data-testid="testimonials-heading"
            className="font-serif-d font-black tracking-tight leading-[1.0] text-4xl sm:text-6xl lg:text-7xl text-[#F3F0E8] mt-6"
          >
            Nice things people{" "}
            <span className="italic font-semibold text-[#F3F0E8]/45">have said.</span>
          </h2>
          <div className="flex items-center gap-5 mt-8">
            <div className="flex -space-x-3" aria-hidden="true">
              {faces.map((f) => (
                <img
                  key={f.name}
                  src={f.photo}
                  alt=""
                  loading="lazy"
                  className="w-10 h-10 rounded-full object-cover border-2 border-[#09090B] grayscale hover:grayscale-0 transition-all duration-300"
                />
              ))}
            </div>
            <p className="text-sm text-[#F3F0E8]/50 max-w-[26ch] leading-snug">
              Founders, coaches, consultants and executives.
            </p>
          </div>
        </Reveal>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 mt-16 items-start">
          {testimonials.map((t, i) => (
            <Reveal key={t.name} delay={(i % 3) * 0.1}>
              <figure
                data-testid={`testimonial-card-${i + 1}`}
                className={`group border border-[#F3F0E8]/12 hover:border-[#118C4F]/50 p-8 sm:p-10 flex flex-col justify-between min-h-[260px] ${t.h} transition-colors duration-500`}
              >
                <div>
                  <span aria-hidden="true" className="font-serif-d font-black text-5xl text-[#118C4F] leading-none">
                    &ldquo;
                  </span>
                  <blockquote className="font-serif-d italic font-semibold text-lg sm:text-xl text-[#F3F0E8]/80 leading-snug mt-2">
                    {t.quote}
                  </blockquote>
                </div>
                <figcaption className="flex items-center justify-between mt-10 pt-6 border-t border-[#F3F0E8]/10">
                  <div className="flex items-center gap-4">
                    <img
                      src={t.photo}
                      alt={`${t.name} — sample avatar`}
                      loading="lazy"
                      className="w-11 h-11 rounded-full object-cover border border-[#F3F0E8]/15 grayscale group-hover:grayscale-0 transition-all duration-500"
                    />
                    <div>
                      <p className="text-sm font-semibold text-[#F3F0E8]/90">{t.name}</p>
                      <p className="text-xs text-[#F3F0E8]/45 mt-0.5">{t.role}</p>
                    </div>
                  </div>
                  <Linkedin className="w-4 h-4 text-[#F3F0E8]/25 group-hover:text-[#118C4F] transition-colors duration-500" aria-hidden="true" />
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>

        <p data-testid="testimonials-sample-note" className="text-[10px] uppercase tracking-[0.25em] text-[#F3F0E8]/25 mt-10">
          Sample testimonials shown for layout — being replaced with real client words
        </p>
      </div>
    </section>
  );
}
